import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalInput, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { useUser } from './UserContext';

const SignInContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${theme.colors.bg};
  padding: 2rem;
`;


const SignInCard = styled(TerminalCard)`
  width: 100%;
  max-width: 450px;
`;

const Title = styled(TerminalTitle)`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: ${theme.fonts.mono};
  font-weight: 600;
  color: ${theme.colors.text};
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const Input = styled(TerminalInput)`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
`;

const Button = styled(TerminalButton)`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-family: ${theme.fonts.mono};
  font-size: 0.9rem;
  text-align: center;
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(255, 0, 64, 0.1);
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.sm};
  
  &::before {
    content: '[ERROR] ';
    color: ${theme.colors.error};
  }
`;

const SignIn = ({ onSignIn }) => {
  const { setUser } = useUser();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);
        localStorage.setItem('authToken', data.token);
        setUser(data.user);
        onSignIn(data.user);
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignInContainer>
      <SignInCard>
        <Title>Authentication Required</Title>
        <TerminalText style={{ textAlign: 'center', marginBottom: '2rem', display: 'block' }}>
          root@quantdelta~# login
        </TerminalText>
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="username">Username</Label>
            <Input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="Enter username..."
            />
          </InputGroup>
          
          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter password..."
            />
          </InputGroup>
          
          <Button type="submit" disabled={loading}>
            {loading ? 'Authenticating...' : 'Login'}
          </Button>
        </Form>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SignInCard>
    </SignInContainer>
  );
};

export default SignIn;
